import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './AIPredictiveAnalytics.module.scss';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import Select from '@/components/ui/Select/Select.tsx';
import { getCo2Analytics, getEntitiesFilters } from '@/api/climatePro.ts';
import { transformDataToGroupedOptionsArray } from '@/pages/ClimateProDashboard/Metrics/Metrics.tsx';
import { periodOptions } from '@/constants/climatePro.ts';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import useDashboardAnalytics from '@/store/useDashboardAnalytics.ts';
import ForecastChart from '@/pages/ClimateProDashboard/AIPredictiveAnalytics/ForecastChart/ForecastChart.tsx';
import Co2SourceModelClass = App.Enums.Co2SourceModelClass;
import Co2Period = App.Enums.Co2Period;
import breakpoints from '@/constants/breakpoints.ts';
import { trackPostHogEvent } from '@/utils/productAnalyticsUtil.ts';

const AiPredictiveAnalytics: React.FC = () => {
	const { organizationEmissionTarget } = useWhoamiStore();

	const [entity, setEntity] = useState<string | null>(null);
	const { setContribution, setTimePeriod, timePeriod } = useDashboardAnalytics();
	const [applyBundleTrips, setApplyBundleTrips] = useState(false);
	const [applyDirectFlights, setApplyDirectFlights] = useState(false);
	const [applyEconomyClass, setApplyEconomyClass] = useState(false);
	const [isMajorChange, setIsMajorChange] = useState(false);
	const isFirstLoad = useRef(true);

	const { data: entitiesFilter, isSuccess } = useQuery({
		queryKey: [queryKeys.entitiesFilter],
		queryFn: getEntitiesFilters,
	});

	const query = useQuery({
		queryKey: [queryKeys.co2Analytics, timePeriod, entity, applyBundleTrips, applyDirectFlights, applyEconomyClass],
		queryFn: async () => {
			const [sourceModelClass, sourceModelId] = entity!.split('.');
			const data = {
				period: timePeriod,
				sourceModelClass: sourceModelClass as Co2SourceModelClass,
				sourceModelId: +sourceModelId,
				applyBundleTrips,
				applyDirectFlights,
				applyEconomyClass,
			};
			setContribution(sourceModelClass as Co2SourceModelClass);
			return await getCo2Analytics(data);
		},
		enabled: !!entity && !!timePeriod,
		placeholderData: previousData => previousData ?? undefined,
	});

	useEffect(() => {
		if (isFirstLoad.current) {
			isFirstLoad.current = false;
		} else {
			setApplyBundleTrips(false);
			setApplyDirectFlights(false);
			setApplyEconomyClass(false);

			setIsMajorChange(true);
			const timer = setTimeout(() => setIsMajorChange(false), 500);
			return () => clearTimeout(timer);
		}
	}, [timePeriod, entity]);

	useEffect(() => {
		if (isSuccess && entitiesFilter?.entities?.[0]?.items?.[0]) {
			const defaultEntity = `${entitiesFilter.entities[0].value}.${entitiesFilter.entities[0].items[0].value}`;
			setEntity(defaultEntity);
		}
	}, [isSuccess, entitiesFilter]);

	useEffect(() => {
		entity && query.refetch();
	}, [entity]);

	useEffect(() => {
		timePeriod && query.refetch();
	}, [timePeriod]);

	const availablePeriodOptions = useMemo(() => {
		return periodOptions.filter(
			option => !(option.value === 'current-period' && organizationEmissionTarget === null),
		);
	}, [organizationEmissionTarget]);

	return (
		<FlexBlock flexDirection="column" className={styles.aiPredictiveWrapper}>
			<FlexBlock
				flexDirection={{
					[breakpoints.zero]: 'column',
					[breakpoints.tablet]: 'row',
				}}
				rowGap="16px"
				justifyContent="space-between"
				className={styles.header}
			>
				<h1>CO₂ forecast analytics</h1>
				<FlexBlock
					flexDirection={{
						[breakpoints.zero]: 'column',
						[breakpoints.tablet]: 'row',
					}}
					justifyContent="space-between"
					columnGap="32px"
					rowGap="12px"
				>
					<Select
						required
						searchable
						value={entity || undefined}
						onChange={value => {
							setEntity(value as string);
							trackPostHogEvent('ai_predictive_analytics_entity_changed', { category: 'forecast-analytics' });
						}}
						name="sourceModelId"
						options={transformDataToGroupedOptionsArray(entitiesFilter?.entities || [])}
						className={styles.selectEntityDropdown}
					/>
					<Select
						required
						value={timePeriod}
						onChange={value => {
							if (value) {
								setTimePeriod(value as Co2Period);
								trackPostHogEvent('ai_predictive_analytics_entity_changed', {
									category: 'forecast-analytics',
								});
							}
						}}
						placeholder="Choose period"
						options={availablePeriodOptions}
						className={styles.selectDropdown}
					/>
				</FlexBlock>
			</FlexBlock>
			<p className={styles.additionalInfo}>The following suggestions would help limit your emissions</p>
			<ForecastChart
				analyticsQuery={query}
				applyBundleTrips={applyBundleTrips}
				setApplyBundleTrips={setApplyBundleTrips}
				applyDirectFlights={applyDirectFlights}
				setApplyDirectFlights={setApplyDirectFlights}
				applyEconomyClass={applyEconomyClass}
				setApplyEconomyClass={setApplyEconomyClass}
				isMajorChange={isMajorChange}
			/>
		</FlexBlock>
	);
};

export default AiPredictiveAnalytics;
